import { AbstractControl } from '@angular/forms';
import { CastObservablePipe } from '@lockton-apps/shared/ui/pipes';
import { defaultInterpolation } from '@lockton-apps/shared/util/proposal-utils';
import { concatLatestFrom } from '@ngrx/operators';
import { FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FormlyHookConfig } from '@ngx-formly/core/lib/models';
import { FormlySelectOption } from '@ngx-formly/core/select';
import { getYear } from 'date-fns';
import {
  compact,
  constant,
  defaults,
  defaultsDeep,
  differenceWith,
  every,
  find,
  findLast,
  flatMap,
  get,
  includes,
  intersectionWith,
  isArray,
  map,
  max,
  mergeWith,
  orderBy,
  reduce,
  set,
  some,
  values,
} from 'lodash';
import {
  catchError,
  filter as rxFilter,
  finalize,
  first,
  firstValueFrom,
  forkJoin,
  map as rxMap,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { DynamicFieldAction } from '../dynamic-field-type/dynamic-field-type.component';
import { ReverseTableRow } from '../reverse-calc-table-type/reverse-calc-table-type.component';
import { COUNTRY_NAMES_DEFAULT } from '@lockton-apps/shared/util/constants';
import {
  QUOTE_WORKFLOW_STATUSES,
  QuoteStatusHistory,
} from '@lockton-apps/shared/interfaces';
import {
  DocumentsService,
  SupportingDocumentsService,
} from '@lockton-apps/shared/data-access/documents';
import {
  ConfirmationDialogComponent,
  FeedbackService,
} from '@lockton-apps/shared/ui/lui';
import { nanoid } from '@lockton-apps/shared/util/helpers';
import { DynamicFormAction } from '../interfaces/dynamic-form.model';
import { MatDialog } from '@angular/material/dialog';

export interface DynamicFormExtension {
  id: string;
  prop: string;
  value: any;
  mappedValue?: (field: FormlyFieldConfig, model: any) => any;
}

export interface DynamicFormHooks {
  id: string;
  hookConfig: FormlyHookConfig;
}

export interface DynamicFormParsers {
  id: string;
  parsers: Function[];
}

export interface DynamicFormExpressions {
  [key: string]: string | ((field: FormlyFieldConfig) => any) | Observable<any>;
}

export interface DynamicFormValidators {
  expression: (c: AbstractControl, field: FormlyFieldConfig) => boolean;
  message?: (error: unknown, field: FormlyFieldConfig) => string;
}

export interface FieldGroupValidator {
  expression: (c: AbstractControl, field: FormlyFieldConfig) => boolean;
  message?: (error: unknown, field: FormlyFieldConfig) => string;
  errorPath?: string;
}

export enum DynamicFormDefaultExtension {
  TableColumnsInterpolation,
  TableMobileLabelsInterpolation,
  ReverseTableRowLabelsInterpolation,
  TableSummaryMobileLabelsInterpolation,
  LabelInterpolation,
  SelectOptionLabelInterpolation,
  HumanizeBooleanValue,
  CountryList,
  FileUploadHandler,
  FileDownloadHandler,
  FileDeleteHandler,
  BeforeFileUploadChange,
}

export enum DynamicFormDefaultExpressions {
  AnyFromTheGroupTrue = 'anyFromTheGroupTrue',
  AnyFromTheGroupFalse = 'anyFromTheGroupFalse',
  AnyFromTheGroupTruethy = 'anyFromTheGroupTruethy',
  AnyFromTheGroupFalsey = 'anyFromTheGroupFalsey',
  AnyFromTheGroupNotTrue = 'anyFromTheGroupNotTrue',
  AnyFromTheGroupNotFalse = 'anyFromTheGroupNotFalse',

  SectionVisibilityAnyFromTheListTruthy = 'sectionVisibilityAnyFromTheListTruthy',
}

export enum DynamicFormDefaultHooks {
  DisableSelectedOptions = 'disableSelectedOptions',
  AutoSelectOption = 'autoSelectOption',
  ClientName = 'clientName',
  FinancialYearDefinitionPrompt = 'fnancialYearDefinitionPrompt',
  ClearSignature = 'clearSignature',
  DeclarationDate = 'declarationDate',
}

export abstract class DynamicFormsExtensionsService {
  readonly dynamicFormAction$: Observable<DynamicFormAction>;

  protected abstract readonly partnerData$: Observable<Record<string, any>>;

  protected readonly defaultExtensions = {
    [DynamicFormDefaultExtension.TableColumnsInterpolation]: {
      id: 'tableColumnsInterpolation',
      prop: 'tableColumns',
      value: [],
      mappedValue: (field: FormlyFieldConfig) =>
        this.defaultInterpolation$(field.props?.['tableColumns']),
    },
    [DynamicFormDefaultExtension.TableSummaryMobileLabelsInterpolation]: {
      id: 'tableSummaryMobileLabelsInterpolation',
      prop: 'summary.mobileLabels',
      value: [],
      mappedValue: (field: FormlyFieldConfig) =>
        this.defaultInterpolation$(field.props?.['summary']?.['mobileLabels']),
    },
    [DynamicFormDefaultExtension.TableMobileLabelsInterpolation]: {
      id: 'tableMobileLabelsInterpolation',
      prop: 'mobileLabels',
      value: [],
      mappedValue: (field: FormlyFieldConfig) =>
        this.defaultInterpolation$(field.props?.['mobileLabels']),
    },
    [DynamicFormDefaultExtension.ReverseTableRowLabelsInterpolation]: {
      id: 'reverseTableRowLabelsInterpolation',
      prop: 'tableRows',
      value: [],
      mappedValue: (field: FormlyFieldConfig) =>
        this.reverseTableRowInterpolation$(field.props?.['tableRows']),
    },
    [DynamicFormDefaultExtension.LabelInterpolation]: {
      id: 'labelInterpolation',
      prop: 'label',
      value: '',
      mappedValue: (field: FormlyFieldConfig) =>
        this.defaultInterpolation$(field.props?.['label']),
    },
    [DynamicFormDefaultExtension.SelectOptionLabelInterpolation]: {
      id: 'selectOptionLabelInterpolation',
      prop: 'options',
      value: '',
      mappedValue: (field: FormlyFieldConfig) =>
        this.selectOptionInterpolation$(
          field.props?.['options'] as FormlySelectOption[],
        ),
    },
    [DynamicFormDefaultExtension.HumanizeBooleanValue]: {
      id: 'humanizeBooleanValue',
      prop: 'displayWith',
      value: (value: boolean) => (value ? $localize`Yes` : $localize`No`),
    },
    [DynamicFormDefaultExtension.CountryList]: {
      id: 'countryList',
      prop: 'options',
      value: map(COUNTRY_NAMES_DEFAULT, (name) => ({
        label: name,
        value: name,
      })),
    },
    [DynamicFormDefaultExtension.FileUploadHandler]: {
      id: 'fileUploadHandler',
      prop: 'fileUploadHandler',
      value: (files: File[]) => {
        return firstValueFrom(
          this.partnerData$.pipe(
            switchMap((partnerData) => {
              const propFormId = get(partnerData, 'quote.propFormId');

              if (!propFormId) {
                console.error('No prop form id');
                return of([]);
              }

              if (!this.supportingDocumentsService || !this.feedbackService) {
                console.error('Missing file upload dependencies');
                return of([]);
              }

              const uploadStreams$ = map(files, (file) =>
                this.supportingDocumentsService!.add({ file, propFormId }).pipe(
                  rxMap((document) => ({
                    fileName: file.name,
                    id: document.id,
                    status: 'success',
                  })),
                  catchError((error) => {
                    console.error('Error uploading document', error);
                    return of({
                      fileName: file.name,
                      id: nanoid(),
                      status: 'error',
                    });
                  }),
                ),
              );

              return forkJoin(uploadStreams$).pipe(
                rxMap((documents) => {
                  return map(documents, (document) => ({
                    name: document.fileName,
                    fileId: document.id,
                    status: document.status,
                  }));
                }),
                tap(() =>
                  this._dynamicFormAction$.next(DynamicFormAction.Save),
                ),
              );
            }),
          ),
        );
      },
    },
    [DynamicFormDefaultExtension.FileDownloadHandler]: {
      id: 'fileDownloadHandler',
      prop: 'fileDownloadHandler',
      value: async (fileId: number, fileName?: string) => {
        if (!this.documentsService) {
          console.error('Missing file download dependencies');
          return;
        }
        if (!this.feedbackService) {
          console.error('Missing feedback service');
        }
        this.feedbackService?.subtleMessage('Downloading file please wait...');
        this.feedbackService?.showLoadingOverlay();
        await firstValueFrom(this.documentsService.download(fileId, fileName));
        this.feedbackService?.hideLoadingOverlay();
      },
    },
    [DynamicFormDefaultExtension.FileDeleteHandler]: {
      id: 'fileDeleteHandler',
      prop: 'fileDeleteHandler',
      value: async (fileId: number) => {
        if (!this.documentsService) {
          console.error('Missing file download dependencies');
          return;
        }
        if (!this.feedbackService) {
          console.error('Missing feedback service');
        }

        this.feedbackService?.showLoadingOverlay();

        return await firstValueFrom(
          this.documentsService.delete(fileId).pipe(
            tap(() => {
              this._dynamicFormAction$.next(DynamicFormAction.Save);
            }),
            catchError((error) => throwError(() => error)),
            finalize(() => {
              this.feedbackService?.hideLoadingOverlay();
            }),
          ),
        );
      },
    },
    [DynamicFormDefaultExtension.BeforeFileUploadChange]: {
      id: 'beforeFileUploadChange',
      prop: 'beforeSelect',
      value: '',
      mappedValue: () => {
        return (field: FormlyFieldConfig) => {
          if (!this.dialog) {
            throw new Error('Missing dialog');
          }

          const fileInputControl: FormlyFieldConfig | undefined = find(
            this.recursivelyFlatFieldGroups(field.parent),
            { type: 'file-input' },
          );

          if (fileInputControl?.formControl?.value?.length) {
            return this.dialog
              .open(ConfirmationDialogComponent, {
                data: {
                  title: $localize`Before you continue`,
                  content: $localize`In order to change your selection you must first remove the uploaded files`,
                  confirmButtonText: $localize`Ok`,
                  dismissible: true,
                },
              })
              .afterClosed()
              .pipe(rxMap(constant(false)));
          }

          return of(true);
        };
      },
    },
  };

  protected readonly defaultExpressions = {
    [DynamicFormDefaultExpressions.AnyFromTheGroupFalse]: {
      hide: (field: FormlyFieldConfig) =>
        !some(
          field.parent?.fieldGroup,
          ({ id }) => id && get(field.model, id, true) === false,
        ),
    },
    [DynamicFormDefaultExpressions.AnyFromTheGroupTrue]: {
      hide: (field: FormlyFieldConfig) =>
        !some(
          field.parent?.fieldGroup,
          ({ id }) => id && get(field.model, id) === true,
        ),
    },
    [DynamicFormDefaultExpressions.AnyFromTheGroupFalsey]: {
      hide: (field: FormlyFieldConfig) =>
        !some(
          field.parent?.fieldGroup,
          ({ id }) => id && !get(field.model, id, true),
        ),
    },
    [DynamicFormDefaultExpressions.AnyFromTheGroupTruethy]: {
      hide: (field: FormlyFieldConfig) =>
        !some(field.parent?.fieldGroup, ({ id }) => id && get(field.model, id)),
    },
    [DynamicFormDefaultExpressions.AnyFromTheGroupNotTrue]: {
      hide: (field: FormlyFieldConfig) =>
        !some(
          field.parent?.fieldGroup,
          ({ id }) => id && get(field.model, id) !== true,
        ),
    },
    [DynamicFormDefaultExpressions.AnyFromTheGroupNotFalse]: {
      hide: (field: FormlyFieldConfig) =>
        !some(
          field.parent?.fieldGroup,
          ({ id }) => id && get(field.model, id) !== false,
        ),
    },
    [DynamicFormDefaultExpressions.SectionVisibilityAnyFromTheListTruthy]: {
      hide: (field: FormlyFieldConfig) =>
        !some(
          field?.props?.['visibility']?.anyFromTheListTruthy,
          (path: string) => get(field.model, path),
        ),
    },
  };

  protected readonly defaultHooks = {
    [DynamicFormDefaultHooks.DisableSelectedOptions]: {
      id: 'disableSelectedOptions',
      hookConfig: {
        afterViewInit: (field: FormlyFieldConfig) => {
          if (!this.castObservable) {
            throw new Error('CatObservablePipe is not provided');
          }

          field.options?.formState
            .externalAction$()
            .pipe(
              rxFilter((action: DynamicFieldAction) =>
                includes(
                  [DynamicFieldAction.Initialize, DynamicFieldAction.Create],
                  action,
                ),
              ),
              concatLatestFrom(() =>
                (this.castObservable as CastObservablePipe).transform(
                  (field.props as FormlyFieldProps).options,
                ),
              ),
              takeUntil(this.destroyNotifier),
            )
            .subscribe(([, options]: any) => {
              if (!field.props) {
                return;
              }

              const selectedOptions = map(
                field.formControl?.parent?.parent?.value,
                field.key as string,
              );

              field.props.options = map(options, (option) => ({
                ...option,
                disabled:
                  includes(selectedOptions, option.value) &&
                  option.value !== field.defaultValue,
              }));
            });
        },
      },
    },
    [DynamicFormDefaultHooks.AutoSelectOption]: {
      id: 'autoSelectOption',
      hookConfig: {
        afterViewInit: (field: FormlyFieldConfig) => {
          if (field.defaultValue) {
            return;
          }

          if (!this.castObservable) {
            throw new Error('CatObservablePipe is not provided');
          }

          field.options?.formState
            .externalAction$()
            .pipe(
              rxFilter((action: DynamicFieldAction) =>
                includes(
                  [DynamicFieldAction.Initialize, DynamicFieldAction.Create],
                  action,
                ),
              ),
              concatLatestFrom(() =>
                (this.castObservable as CastObservablePipe).transform(
                  (field.props as FormlyFieldProps).options,
                ),
              ),
              takeUntil(this.destroyNotifier),
            )
            .subscribe(([, options]: any) => {
              if (field.formControl?.value) {
                return;
              }

              const selectedOptions = map(
                field.formControl?.parent?.parent?.value,
                field.key as string,
              );

              field.formControl?.setValue(
                (
                  max(
                    differenceWith(
                      options,
                      selectedOptions,
                      ({ value }, option) => value === option,
                    ),
                  ) as FormlySelectOption
                ).value,
              );
            });
        },
      },
    },
    [DynamicFormDefaultHooks.ClientName]: {
      id: 'clientName',
      hookConfig: {
        onInit: async (field: FormlyFieldConfig) => {
          if (!field.formControl) {
            return;
          }

          if (!field.formControl.value) {
            const data = await firstValueFrom(this.partnerData$.pipe(first()));

            field.formControl.setValue(data['clientName']);
          }
        },
      },
    },
    [DynamicFormDefaultHooks.FinancialYearDefinitionPrompt]: {
      id: 'financialYearDefinitionPrompt',
      hookConfig: {
        onInit: (field: FormlyFieldConfig) => {
          const hasAllFinancialYearsDefined: boolean = every(
            field.props?.['financialYearDefinitionPrompt'],
            (path) => get(field.model, path),
          );
          const promptExistsAlready: boolean =
            field.parent!.fieldGroup?.[(field as any).index - 1]?.props?.[
              'isPrompt'
            ];

          if (hasAllFinancialYearsDefined || promptExistsAlready) {
            return;
          }

          const prompt: FormlyFieldConfig<any> = {
            type: 'template',
            className: 'd-block p-2 bg-blueLow rounded',
            props: {
              isPrompt: true,
              routedLabel: [
                {
                  text: 'Please define your financial years ',
                },
                {
                  text: 'in step 2.1',
                  route: ['../', 'financial-years'],
                  fragment: `section-financialYears.overview`,
                },
              ],
            },
          };

          field.parent!.fieldGroup!.splice((field as any).index, 0, prompt);
          field.options!.build?.();
        },
      },
    },
    [DynamicFormDefaultHooks.ClearSignature]: {
      id: 'clearSignature',
      hookConfig: {
        onInit: (field: FormlyFieldConfig) => {
          this.partnerData$.pipe(first()).subscribe((data) => {
            if (!field.options) return;
            console.log(data);
            if (
              field.options.formState['signatureInitialised'] ||
              field.options.formState['isPrint'] ||
              data['readonly']
            )
              return;
            console.log('Clearing signature field', field.key);

            field.formControl?.setValue('');
            field.options.formState = {
              ...field.options.formState,
              signatureInitialised: true,
            };
          });
        },
      },
    },
    [DynamicFormDefaultHooks.DeclarationDate]: {
      id: 'declarationDate',
      hookConfig: {
        onInit: async (field: FormlyFieldConfig) => {
          if (!field.formControl) {
            return;
          }

          const data = await firstValueFrom(this.partnerData$.pipe(first()));
          const orderedStatusHistory: QuoteStatusHistory[] = orderBy(
            data['quote'].statusHistory,
            'id',
          );
          const isReturned: boolean =
            data['quote'].workflowStatus ===
            QUOTE_WORKFLOW_STATUSES.ProposalReleased;
          const proposalCompletedDate: string | undefined = findLast(
            orderedStatusHistory,
            { workflowStatus: QUOTE_WORKFLOW_STATUSES.ProposalComplete },
          )?.timestamp;
          const date: string | Date =
            (data['isContact'] && isReturned) || !proposalCompletedDate
              ? new Date()
              : proposalCompletedDate;

          field.formControl.setValue(date);
        },
      },
    },
  };

  protected readonly extensions: DynamicFormExtension[] = [];
  protected readonly hooks: DynamicFormHooks[] = [];
  protected readonly parsers: DynamicFormParsers[] = [];
  protected readonly expressions: Record<string, DynamicFormExpressions> =
    this.defaultExpressions;
  protected readonly validators: Record<string, DynamicFormValidators> = {};
  protected readonly fieldGroupValidators: Record<string, FieldGroupValidator> =
    {};
  protected readonly schedulers: Map<string, Subscription> = new Map();

  readonly destroyNotifier: Subject<void> = new Subject();

  private readonly _dynamicFormAction$: Subject<DynamicFormAction>;

  protected constructor(
    protected readonly castObservable?: CastObservablePipe,
    protected readonly documentsService?: DocumentsService,
    protected readonly supportingDocumentsService?: SupportingDocumentsService,
    protected readonly feedbackService?: FeedbackService,
    protected readonly dialog?: MatDialog,
  ) {
    this._dynamicFormAction$ = new Subject();
    this.dynamicFormAction$ = this._dynamicFormAction$.asObservable();
  }

  get allExtensions() {
    return [...values(this.defaultExtensions), ...this.extensions];
  }

  get allHooks() {
    return [...values(this.defaultHooks), ...this.hooks];
  }

  get allExpressions() {
    return defaults(this.defaultExpressions, this.expressions);
  }

  /** Set mapped extensions and returns field with updated props */
  setExtension<T extends FormlyFieldConfig>(
    field: T,
    extensionIds: string[],
    data: any,
  ) {
    const extensions = intersectionWith(
      this.allExtensions,
      extensionIds,
      (extension, id) => extension.id === id,
    );

    if (!extensions.length) {
      return field;
    }

    return this.updateFieldWithExtensions(field, extensions, data);
  }

  /** Set and return field with mapped hooks */
  setHooks<T extends FormlyFieldConfig>(field: T, hookKey: string): T {
    const hooks = find(this.allHooks, { id: hookKey });
    return hooks ? defaultsDeep({ hooks: hooks.hookConfig }, field) : field;
  }

  /** Set and return field with mapped parsers */
  setParsers<T extends FormlyFieldConfig>(field: T, parserKey: string): T {
    const parsers = find(this.parsers, { id: parserKey });
    return parsers ? defaultsDeep({ parsers: parsers.parsers }, field) : field;
  }

  getExpressions(expressionsIds: string | string[]) {
    if (isArray(expressionsIds)) {
      const expressions = compact(
        map(expressionsIds, (id) => get(this.allExpressions, id)),
      );

      return this.mergeExpressions(expressions);
    }

    return get(this.allExpressions, expressionsIds);
  }

  getValidators(validatorIds: string | string[]) {
    if (isArray(validatorIds)) {
      return compact(map(validatorIds, (id) => get(this.validators, id))).map(
        (validator) => ({
          expression: validator.expression,
          message: validator.message,
        }),
      );
    }

    return get(this.validators, validatorIds);
  }

  /** Set and return field with mapped expressions */
  setExpressions<T extends FormlyFieldConfig>(
    target: T,
    expressionsIds: string | string[],
  ): T {
    const expressions = this.getExpressions(expressionsIds);
    return expressions
      ? defaultsDeep({ expressions: expressions }, target)
      : target;
  }

  setValidators<T extends FormlyFieldConfig>(
    target: T,
    validatorIds: string | string[],
  ): T {
    const validators = this.getValidators(validatorIds);

    return validators
      ? defaultsDeep(
          {
            validators: validators,
          },
          target,
        )
      : target;
  }

  setFieldGroupValidator<T extends FormlyFieldConfig>(
    target: T,
    validatorId: string,
  ): T {
    const validatorOptions = get(this.fieldGroupValidators, validatorId);

    const validation = [
      {
        name: 'customValidator',
        options: validatorOptions,
      },
    ];

    return validation
      ? defaultsDeep({ validators: { validation } }, target)
      : target;
  }

  private defaultInterpolation$(target: string[] | string | undefined) {
    return this.partnerData$.pipe(
      rxMap((data) =>
        defaultInterpolation(
          target,
          defaults(
            {
              currentYear: getYear(new Date().getTime()),
              proposalCreatedYear: getYear(
                new Date(data['createdDate']).getTime(),
              ),
            },
            data['financialYears'],
          ),
        ),
      ),
    );
  }

  private reverseTableRowInterpolation$(target: ReverseTableRow[] | undefined) {
    return this.partnerData$.pipe(
      rxMap((data) =>
        map(target, (row) => ({
          ...row,
          label: defaultInterpolation(
            row.label,
            defaults(
              {
                currentYear: getYear(new Date().getTime()),
                proposalCreatedYear: getYear(
                  new Date(data['createdDate']).getTime(),
                ),
              },
              data['financialYears'],
            ),
          ),
        })),
      ),
    );
  }

  // TODO: As per above interpolations update with interpolation args
  private selectOptionInterpolation$(target: FormlySelectOption[] | undefined) {
    return this.partnerData$.pipe(
      rxMap((data) =>
        map(target, (option) => ({
          ...option,
          label: defaultInterpolation(option.label, {
            currentYear: getYear(new Date(data['createdDate']).getTime()),
          }),
          value: defaultInterpolation(option.value, {
            currentYear: getYear(new Date(data['createdDate']).getTime()),
          }),
        })),
      ),
    );
  }

  private mergeExpressions(expressions: DynamicFormExpressions[]) {
    return reduce(expressions, (mergedExp, currentExp) => {
      return mergeWith(mergedExp, currentExp, (mergedFn, fn) => {
        return (field: any) => {
          if (!mergedFn) {
            return fn(field);
          }
          return mergedFn(field) || fn(field);
        };
      });
    });
  }

  /** Update field props with mapped extension */
  private updateFieldWithExtensions<T extends FormlyFieldConfig>(
    field: T,
    extensions: DynamicFormExtension[],
    data: any,
  ): T {
    return reduce(
      extensions,
      (updatedField, extension) => {
        const newValue = extension.mappedValue
          ? extension.mappedValue(field, data)
          : extension.value;

        return defaultsDeep(
          { props: set({}, extension.prop, newValue) },
          updatedField,
        );
      },
      field,
    );
  }

  private recursivelyFlatFieldGroups(
    field?: FormlyFieldConfig,
  ): FormlyFieldConfig[] {
    return flatMap(flatMap(field?.fieldGroup), (item: FormlyFieldConfig) =>
      item.fieldGroup ? this.recursivelyFlatFieldGroups(item) : item,
    );
  }
}
